import { useCallback } from 'react'

import { MediaInfoDialog } from '@/components/shared/media-info-dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { db, type MusicPlaylist, type StoredExternalMusic } from '@/lib/db'
import { formatDuration } from '@/lib/time-format'

type MusicInfoDialogProps = {
  music: StoredExternalMusic | null
  onClose: () => void
  onSaved: () => void
  /** 全プレイリスト */
  playlists: MusicPlaylist[]
  /** 対象曲が所属するプレイリストIDセット */
  memberPlaylistIds: Set<string>
  /** チェック切り替え */
  onTogglePlaylist: (playlistId: string, musicId: string, checked: boolean) => void
}

const handleUpdate = async (id: string, changes: { title: string; memo: string }) => {
  await db.externalMusic.update(id, changes)
}

export const MusicInfoDialog = ({
  music,
  onClose,
  onSaved,
  playlists,
  memberPlaylistIds,
  onTogglePlaylist,
}: MusicInfoDialogProps) => {
  const renderExtraFields = useCallback(
    (item: StoredExternalMusic) => (
      <>
        {item.artist && (
          <div>
            <Label className="text-xs text-muted-foreground">アーティスト</Label>
            <p className="text-sm">{item.artist}</p>
          </div>
        )}
        {item.duration != null && (
          <div>
            <Label className="text-xs text-muted-foreground">再生時間</Label>
            <p className="text-sm">{formatDuration(item.duration)}</p>
          </div>
        )}

        {/* プレイリスト割り当て */}
        {playlists.length > 0 && (
          <>
            <Separator />
            <div>
              <Label className="text-xs text-muted-foreground">プレイリスト</Label>
              <div className="mt-2 space-y-2">
                {playlists.map((pl) => (
                  <label key={pl.id} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={memberPlaylistIds.has(pl.id)}
                      onCheckedChange={(checked) => onTogglePlaylist(pl.id, item.id, !!checked)}
                    />
                    <span className="text-sm">{pl.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}
      </>
    ),
    [playlists, memberPlaylistIds, onTogglePlaylist],
  )

  return (
    <MediaInfoDialog
      item={music}
      dialogTitle="音楽情報"
      onUpdate={handleUpdate}
      onClose={onClose}
      onSaved={onSaved}
      renderExtraFields={renderExtraFields}
    />
  )
}
